import {
	Badge,
	Button,
	Card,
	Drawer,
	Empty,
	Flex,
	Popconfirm,
	Popover,
	Segmented,
	Space,
	Spin,
	Tag,
	Typography,
	message,
} from 'antd';
import {
	ArrowLeftOutlined,
	CloudDownloadOutlined,
	DownOutlined,
	InboxOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
	CertProposal,
	useConfirmReadyCertsMutation,
	useGetCertPoGroupsQuery,
	useGetCertProposalsQuery,
	useGetCertSummaryQuery,
} from '../../redux/api/material-certs';
import { BUCKETS, BUCKET_ORDER, bucketCount, type QueueFilter } from './buckets';
import FetchPanel from './FetchPanel';
import PdfPane from './PdfPane';
import PoList from './PoList';
import PoTable from './PoTable';
import ProposalDetail from './ProposalDetail';
import RegisterTable from './RegisterTable';
import UploadPanel from './UploadPanel';

const { Title, Text } = Typography;

const errorDetail = (error: unknown, fallback: string) =>
	(error as { data?: { detail?: string } })?.data?.detail || fallback;

/** Review one PO's heats, browse every PO, or audit the whole register. */
type CertView = 'queue' | 'pos' | 'register';

/**
 * The mill-certificate inbox for Alro and McMaster-Carr.
 *
 * The queue starts as a PO list. Clicking a PO drills into its heats; Back
 * returns to the list. The All POs tab is the searchable table when the
 * question is which PO a bar belongs to. Nothing reaches ERP until someone
 * matches.
 */
export default function MaterialCertificates() {
	const [view, setView] = useState<CertView>('queue');
	const [leftPane, setLeftPane] = useState<'pos' | 'heats'>('pos');
	const [fetchOpen, setFetchOpen] = useState(false);
	const [uploadOpen, setUploadOpen] = useState(false);
	const [poOpen, setPoOpen] = useState(false);
	const [selectedPo, setSelectedPo] = useState<string | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [showDone, setShowDone] = useState(false);
	const [reassigning, setReassigning] = useState(false);
	const [bucket, setBucket] = useState<QueueFilter>('all');

	const openPo = useCallback((po: string) => {
		setSelectedPo(po);
		setSelectedId(null);
		setBucket('all');
		setLeftPane('heats');
	}, []);

	const { data: summary } = useGetCertSummaryQuery();
	const {
		data: groups = [],
		isFetching: loadingGroups,
		refetch: refetchGroups,
	} = useGetCertPoGroupsQuery();

	const visibleGroups = useMemo(
		() => (showDone ? groups : groups.filter((group) => group.pending_count > 0)),
		[groups, showDone]
	);

	// Rejected heats are fetched only when the reviewer asks for them, so the
	// ordinary queue is exactly the work outstanding.
	const { data: proposals = [], isFetching: loadingProposals } =
		useGetCertProposalsQuery(
			{ po: selectedPo ?? undefined, include_rejected: bucket === 'rejected' },
			{ skip: !selectedPo }
		);

	const activeGroup = groups.find((group) => group.po === selectedPo);

	// Already-certified heats are finished, not work, so they stay out of the
	// default list and sit behind their own tab for anyone auditing.
	const outstanding = useMemo(
		() =>
			proposals.filter(
				(row) => row.bucket !== 'already_certified' && row.status !== 'rejected'
			),
		[proposals]
	);
	const filtered = useMemo(() => {
		if (bucket === 'all') return outstanding;
		if (bucket === 'rejected') {
			return proposals.filter((row) => row.status === 'rejected');
		}
		return proposals.filter(
			(row) => row.bucket === bucket && row.status !== 'rejected'
		);
	}, [proposals, outstanding, bucket]);

	const selected =
		filtered.find((row) => row.id === selectedId) ?? filtered[0] ?? null;

	useEffect(() => {
		if (selected && selected.id !== selectedId) setSelectedId(selected.id);
	}, [selected, selectedId]);

	const [confirmReady, { isLoading: confirmingAll }] =
		useConfirmReadyCertsMutation();

	const move = useCallback(
		(step: number) => {
			if (!filtered.length) return;
			const index = filtered.findIndex((row) => row.id === selected?.id);
			const next = Math.min(
				Math.max((index === -1 ? 0 : index) + step, 0),
				filtered.length - 1
			);
			setSelectedId(filtered[next].id);
		},
		[filtered, selected]
	);

	const position = filtered.findIndex((row) => row.id === selected?.id);
	const hasNext = position > -1 && position < filtered.length - 1;

	// A PO runs out of heats long before the day runs out of work, so Next
	// rolls onto the following PO instead of going dead on the last row.
	const poIndex = visibleGroups.findIndex((group) => group.po === selectedPo);
	const nextPo = poIndex > -1 ? visibleGroups[poIndex + 1] ?? null : null;
	const goToNextPo = useCallback(() => {
		if (!nextPo) return;
		openPo(nextPo.po);
	}, [nextPo, openPo]);

	// Arrow keys move between heats and nothing else. Every action that writes
	// to ERP is a button on purpose: a stray keystroke should never be able to
	// attach or drop a certificate.
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			const target = event.target;
			const typing =
				target instanceof Element &&
				target.closest('input, textarea, [contenteditable="true"]');
			if (
				view !== 'queue' ||
				leftPane !== 'heats' ||
				fetchOpen ||
				poOpen ||
				reassigning ||
				typing ||
				event.metaKey ||
				event.ctrlKey ||
				event.altKey
			) {
				return;
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				move(1);
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				move(-1);
			}
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [move, reassigning, view, leftPane, fetchOpen, poOpen]);

	const bulkConfirm = async () => {
		if (!selectedPo) return;
		try {
			const result = await confirmReady(selectedPo).unwrap();
			if (result.confirmed.length) {
				message.success(
					`Attached ${result.confirmed.length} ${
						result.confirmed.length === 1 ? 'certificate' : 'certificates'
					}.`
				);
			}
			result.errors.forEach((error) => message.warning(error.detail));
			if (result.packet_layer_sync_failed) {
				message.warning(
					'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
				);
			}
			if (!result.confirmed.length && !result.errors.length) {
				message.info('Nothing was ready to match.');
			}
		} catch (error) {
			message.error(errorDetail(error, 'Bulk match failed.'));
		}
	};

	return (
		<div style={{ padding: 16, height: '100%' }}>
			<Flex justify="space-between" align="center" wrap gap={8}>
				<Space align="center">
					<Title level={4} style={{ margin: 0 }}>
						Material Certificates
					</Title>
					{summary && (
						<Text type="secondary">
							{summary.pending_count} heat
							{summary.pending_count === 1 ? '' : 's'} waiting across{' '}
							{summary.pending_po_count} PO
							{summary.pending_po_count === 1 ? '' : 's'}
							{summary.ready_count > 0 && `, ${summary.ready_count} ready`}
						</Text>
					)}
				</Space>
				<Space>
					<Segmented
						value={view}
						onChange={(value) => setView(value as CertView)}
						options={[
							{ value: 'queue', label: 'Review queue' },
							{ value: 'pos', label: 'All POs' },
							{ value: 'register', label: 'All certificates' },
						]}
					/>
					<Button
						icon={<InboxOutlined />}
						onClick={() => setUploadOpen(true)}
					>
						Upload files
					</Button>
					<Button
						icon={<CloudDownloadOutlined />}
						onClick={() => setFetchOpen(true)}
					>
						Fetch from mailbox
					</Button>
					<Button
						icon={<ReloadOutlined />}
						onClick={() => refetchGroups()}
						loading={loadingGroups}
					>
						Refresh
					</Button>
				</Space>
			</Flex>

			<Drawer
				title="Upload files"
				width={480}
				open={uploadOpen}
				onClose={() => setUploadOpen(false)}
			>
				<UploadPanel onUploaded={() => refetchGroups()} />
			</Drawer>

			<Drawer
				title="Fetch from mailbox"
				width={480}
				open={fetchOpen}
				onClose={() => setFetchOpen(false)}
			>
				<FetchPanel />
			</Drawer>

			{view === 'register' ? (
				<div style={{ marginTop: 12, height: 'calc(100% - 64px)' }}>
					<RegisterTable />
				</div>
			) : view === 'pos' ? (
				<div style={{ marginTop: 12, height: 'calc(100% - 64px)' }}>
					<PoTable
						selectedPo={selectedPo}
						onSelect={(po) => {
							openPo(po);
							setView('queue');
						}}
					/>
				</div>
			) : (
			<Flex gap={12} align="stretch" style={{ marginTop: 12, height: 'calc(100% - 64px)' }}>
				<Card
					size="small"
					style={{ width: 300, flex: '0 0 300px', overflow: 'auto' }}
					styles={{ body: { padding: 0 } }}
					title={
						leftPane === 'pos' ? (
							<Text strong>POs</Text>
						) : (
							<Space size={4}>
								<Button
									type="text"
									size="small"
									icon={<ArrowLeftOutlined />}
									onClick={() => setLeftPane('pos')}
								>
									Back
								</Button>
								<Popover
									open={poOpen}
									onOpenChange={setPoOpen}
									trigger="click"
									placement="bottomLeft"
									overlayStyle={{ width: 340 }}
									styles={{ body: { padding: 8 } }}
									content={
										<div>
											<Flex
												justify="space-between"
												align="center"
												style={{ marginBottom: 8 }}
											>
												<Text type="secondary" style={{ fontSize: 12 }}>
													{visibleGroups.length} PO
													{visibleGroups.length === 1 ? '' : 's'}
												</Text>
												<Button
													type="link"
													size="small"
													style={{ padding: 0 }}
													onClick={() => setShowDone((on) => !on)}
												>
													{showDone ? 'Hide done' : 'Show done'}
												</Button>
											</Flex>
											<div style={{ maxHeight: 420, overflow: 'auto' }}>
												<PoList
													groups={visibleGroups}
													loading={loadingGroups && !groups.length}
													selectedPo={selectedPo}
													onSelect={(po) => {
														openPo(po);
														setPoOpen(false);
													}}
												/>
											</div>
										</div>
									}
								>
									<Button type="text" size="small" style={{ padding: '0 4px' }}>
										<Text strong>
											{selectedPo ? `PO ${selectedPo}` : 'Pick a PO'}
										</Text>
										<DownOutlined
											style={{ fontSize: 10, marginInlineStart: 4 }}
										/>
									</Button>
								</Popover>
							</Space>
						)
					}
					extra={
						leftPane === 'pos' ? (
							<Button
								type="link"
								size="small"
								style={{ padding: 0 }}
								onClick={() => setShowDone((on) => !on)}
							>
								{showDone ? 'Hide done' : 'Show done'}
							</Button>
						) : null
					}
				>
					{leftPane === 'pos' ? (
						<PoList
							groups={visibleGroups}
							loading={loadingGroups && !groups.length}
							selectedPo={selectedPo}
							onSelect={openPo}
						/>
					) : (
						<>
							{selectedPo && (
								<Flex
									align="center"
									gap={8}
									wrap
									style={{ padding: 8 }}
								>
									<Segmented
										size="small"
										value={bucket}
										onChange={(value) => setBucket(value as QueueFilter)}
										style={{ flex: 1, minWidth: 0 }}
										options={[
											{ value: 'all', label: `All ${outstanding.length}` },
											...BUCKET_ORDER.filter(
												(key) =>
													!activeGroup || bucketCount(activeGroup, key) > 0
											).map((key) => ({
												value: key,
												label: BUCKETS[key].short,
											})),
											// Only offered once there is something to recover, so
											// the control does not advertise a shelf that is empty.
											...(activeGroup && activeGroup.rejected_count > 0
												? [
														{
															value: 'rejected' as const,
															label: `Rejected ${activeGroup.rejected_count}`,
														},
													]
												: []),
										]}
									/>
									{activeGroup && activeGroup.ready_count > 0 && (
										<Popconfirm
											title={`Attach ${activeGroup.ready_count} certificate${
												activeGroup.ready_count === 1 ? '' : 's'
											}?`}
											description="Only the heats marked ready are touched."
											onConfirm={bulkConfirm}
										>
											<Button
												type="primary"
												size="small"
												loading={confirmingAll}
											>
												Match ready
											</Button>
										</Popconfirm>
									)}
								</Flex>
							)}
							{loadingProposals && !proposals.length ? (
								<Flex justify="center" style={{ padding: 24 }}>
									<Spin />
								</Flex>
							) : !filtered.length ? (
								<Empty
									style={{ padding: 24 }}
									description={selectedPo ? 'Nothing here.' : 'Pick a PO.'}
								/>
							) : (
								filtered.map((row) => (
									<HeatRow
										key={row.id}
										proposal={row}
										isSelected={row.id === selected?.id}
										onSelect={() => setSelectedId(row.id)}
									/>
								))
							)}
						</>
					)}
				</Card>

				{/* The paperwork is the slow part of the job, so it gets the height
				    rather than sitting in a strip under the details. */}
				<Flex gap={12} align="stretch" style={{ flex: 1, minWidth: 0 }}>
					<Card
						size="small"
						style={{ width: 400, flex: '0 0 400px', overflow: 'auto' }}
						styles={{ body: { padding: 12 } }}
					>
						{selected ? (
							<ProposalDetail
								proposal={selected}
								reassigning={reassigning}
								setReassigning={setReassigning}
								onNext={() => move(1)}
								hasNext={hasNext}
								onNextPo={goToNextPo}
								hasNextPo={Boolean(nextPo)}
							/>
						) : (
							<Empty
								description={
									leftPane === 'pos'
										? 'Pick a PO on the left.'
										: 'Pick a heat on the left.'
								}
							/>
						)}
					</Card>
					<Card
						size="small"
						style={{ flex: 1, minWidth: 0 }}
						styles={{
							body: { padding: 8, height: '100%', boxSizing: 'border-box' },
						}}
					>
						<PdfPane proposal={selected} />
					</Card>
				</Flex>
			</Flex>
			)}
		</div>
	);
}

function HeatRow({
	proposal,
	isSelected,
	onSelect,
}: {
	proposal: CertProposal;
	isSelected: boolean;
	onSelect: () => void;
}) {
	const bucket = BUCKETS[proposal.bucket];
	const title =
		proposal.vendor_key === 'mcmaster'
			? `Line ${proposal.line_number ?? '?'}${proposal.edp ? ` · ${proposal.edp}` : ''}`
			: proposal.heat
				? `HT# ${proposal.heat}`
				: 'Requested cert';
	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onSelect}
			onKeyDown={(event) => {
				if (event.key === 'Enter') onSelect();
			}}
			style={{
				padding: '8px 12px',
				cursor: 'pointer',
				borderLeft: `3px solid ${isSelected ? '#1677ff' : 'transparent'}`,
				background: isSelected ? '#e6f4ff' : undefined,
				borderBottom: '1px solid #f0f0f0',
			}}
		>
			<Flex justify="space-between" align="center" gap={8}>
				<Space size={4}>
					<Text strong ellipsis style={{ maxWidth: 170 }}>
						{title}
					</Text>
					{proposal.requested && (
						<Tag color="purple" style={{ marginInlineEnd: 0 }}>
							Requested
						</Tag>
					)}
				</Space>
				{proposal.status === 'confirmed' ? (
					<Badge status="success" text="Attached" />
				) : proposal.status === 'rejected' ? (
					<Tag style={{ marginInlineEnd: 0 }}>Rejected</Tag>
				) : (
					<Tag color={bucket.color} style={{ marginInlineEnd: 0 }}>
						{bucket.label}
					</Tag>
				)}
			</Flex>
			<Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
				{proposal.description || proposal.rt}
			</Text>
		</div>
	);
}
